<template>
  <div class="page-content">
    <div class="page-header no-print">
      <div>
        <BreadcrumbComponent :items="breadcrumbs" />
        <h1 class="page-title">
          Commande #{{ order ? order.order_number : '' }}
          <v-chip v-if="order" x-small :color="orderStatusChipColor" dark class="ml-2">
            {{ orderStatusLabel }}
          </v-chip>
        </h1>
      </div>
      <div class="d-flex" style="gap: 8px;">
        <v-btn
          v-if="order && (order.status || '').toLowerCase() === 'open'"
          color="error"
          small
          @click="openCancelDialog"
        >
          <v-icon left small>mdi-cancel</v-icon>
          Annuler la commande
        </v-btn>
        <v-btn color="error" outlined small @click="exportPDF">
          <v-icon left small>mdi-file-pdf-box</v-icon>
          Exporter PDF
        </v-btn>
        <v-btn text @click="$router.push('/restaurant/orders')">
          <v-icon left small>mdi-arrow-left</v-icon>
          Retour
        </v-btn>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable && order">
      <v-row>
        <!-- Left side: Order details -->
        <v-col cols="12" md="7">
          <!-- Summary cards -->
          <v-row class="mb-3">
            <v-col cols="6" sm="3">
              <v-card flat class="stat-card pa-3">
                <div class="stat-label">Date ouverture</div>
                <div class="stat-value">{{ formatDate(order.opened_at || order.created_at) }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card flat class="stat-card pa-3">
                <div class="stat-label">Serveur</div>
                <div class="stat-value">{{ serverName(order) }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card flat class="stat-card pa-3">
                <div class="stat-label">Table</div>
                <div class="stat-value">{{ order.table_id || '—' }}</div>
              </v-card>
            </v-col>
            <v-col cols="6" sm="3">
              <v-card flat class="stat-card pa-3">
                <div class="stat-label">Couverts</div>
                <div class="stat-value">{{ order.guest_count || 0 }}</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Client (visible à l’écran et à l’impression PDF) -->
          <v-card flat class="table-card mb-3 order-client-card">
            <div class="pa-3 d-flex align-center">
              <v-icon small color="primary" class="mr-2">mdi-account-outline</v-icon>
              <span class="font-weight-bold" style="font-size: 0.875rem;">Client</span>
            </div>
            <v-divider></v-divider>
            <div class="pa-3 customer-detail-grid">
              <div class="customer-detail-row">
                <span class="customer-detail-label">Nom</span>
                <span class="customer-detail-value">{{ customerDisplayName }}</span>
              </div>
              <div class="customer-detail-row">
                <span class="customer-detail-label">Téléphone</span>
                <span class="customer-detail-value">{{ customerPhone || '—' }}</span>
              </div>
              <div class="customer-detail-row" v-if="customerEmail">
                <span class="customer-detail-label">E-mail</span>
                <span class="customer-detail-value">{{ customerEmail }}</span>
              </div>
              <div class="customer-detail-row">
                <span class="customer-detail-label">Adresse</span>
                <span class="customer-detail-value">{{ customerAddress || '—' }}</span>
              </div>
            </div>
          </v-card>

          <!-- Total card -->
          <v-row class="mb-3">
            <v-col cols="12">
              <v-card flat class="stat-card pa-3">
                <div class="stat-label">Total</div>
                <div class="stat-value font-weight-bold" style="font-size: 1.25rem;">{{ fmt(order.total) }}</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Notes -->
          <v-card v-if="order.notes" flat class="table-card mb-3">
            <div class="pa-3">
              <span class="font-weight-bold" style="font-size: 0.875rem;">
                <v-icon small class="mr-1">mdi-note-text</v-icon>
                Notes
              </span>
            </div>
            <v-divider></v-divider>
            <div class="pa-3" style="font-size: 0.8125rem;">{{ order.notes }}</div>
          </v-card>

          <!-- Items table grouped by category -->
          <v-card flat class="table-card">
            <div class="pa-3 d-flex justify-space-between align-center">
              <span class="font-weight-bold" style="font-size: 0.875rem;">Articles</span>
              <v-chip x-small color="primary" dark>{{ (order.order_products || []).length }} article(s)</v-chip>
            </div>
            <v-divider></v-divider>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th class="text-left">PRODUIT</th>
                  <th class="text-center">QT&Eacute;</th>
                  <th class="text-right">PRIX UNITAIRE</th>
                  <th class="text-right">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(items, category) in groupedProducts">
                  <tr :key="'cat-' + category" class="category-row">
                    <td colspan="4" class="font-weight-bold">{{ category }}</td>
                  </tr>
                  <tr v-for="p in items" :key="'prod-' + p.id">
                    <td class="text-left pl-6">
                      <span class="font-weight-medium">{{ p.product_name }}</span>
                      <br v-if="p.notes" />
                      <span v-if="p.notes" class="text-caption" style="color: #FF9800; font-style: italic;">* {{ p.notes }}</span>
                    </td>
                    <td class="text-center">{{ p.quantity }}</td>
                    <td class="text-right">{{ fmt(p.price) }}</td>
                    <td class="text-right font-weight-medium">{{ fmt(p.total) }}</td>
                  </tr>
                </template>
                <tr v-if="!order.order_products || order.order_products.length === 0">
                  <td colspan="4" class="text-center text--secondary pa-4">Aucun article</td>
                </tr>
              </tbody>
              <tfoot v-if="order.order_products && order.order_products.length > 0">
                <tr class="totals-foot">
                  <td colspan="3" class="text-right">Sous-total</td>
                  <td class="text-right">{{ fmt(order.subtotal) }}</td>
                </tr>
                <tr class="totals-foot" v-if="order.taxe_percent > 0">
                  <td colspan="3" class="text-right">Taxe ({{ order.taxe_percent }}%)</td>
                  <td class="text-right">{{ fmt(order.taxe_amount) }}</td>
                </tr>
                <tr class="totals-foot" v-if="order.service_fee_percent > 0">
                  <td colspan="3" class="text-right">Service ({{ order.service_fee_percent }}%)</td>
                  <td class="text-right">{{ fmt(order.service_fee_amount) }}</td>
                </tr>
                <tr class="totals-foot">
                  <td colspan="3" class="font-weight-bold text-right">TOTAL</td>
                  <td class="text-right font-weight-bold">{{ fmt(order.total) }}</td>
                </tr>
              </tfoot>
            </v-simple-table>
          </v-card>
        </v-col>

        <!-- Right side: Activity log -->
        <v-col cols="12" md="5" class="no-print">
          <v-card flat class="table-card">
            <div class="pa-3">
              <span class="font-weight-bold" style="font-size: 0.875rem;">
                <v-icon small class="mr-1">mdi-history</v-icon>
                Historique
              </span>
            </div>
            <v-divider></v-divider>
            <div class="log-container">
              <div v-for="log in (order.logs || [])" :key="log.id" class="log-entry">
                <div class="log-dot" :class="logColor(log.action)"></div>
                <div class="log-content">
                  <div class="log-action">
                    <v-icon x-small class="mr-1">{{ logIcon(log.action) }}</v-icon>
                    {{ log.description }}
                  </div>
                  <div class="log-meta">
                    <span v-if="log.performed_by">{{ log.performed_by }}</span>
                    <span v-if="log.supervisor_code"> — Code: {{ log.supervisor_code }}</span>
                    <span class="ml-2">{{ formatTime(log.created_at) }}</span>
                  </div>
                </div>
              </div>
              <div v-if="!order.logs || order.logs.length === 0" class="pa-4 text-center text--secondary">
                Aucune activit&eacute;
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <div v-if="enable && !order" class="text-center pa-6">
      <v-icon size="48" color="grey lighten-1">mdi-receipt</v-icon>
      <p class="mt-3 text--secondary">Commande introuvable.</p>
    </div>

    <!-- Cancel order dialog -->
    <v-dialog v-model="cancelDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="error">mdi-cancel</v-icon>
          Annuler la commande
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">
            Cette action est irréversible. La table sera libérée et la commande
            marquée comme annulée. Veuillez indiquer la raison.
          </p>
          <v-textarea
            v-model="cancelReason"
            label="Commentaire / Raison"
            outlined
            dense
            rows="3"
            auto-grow
            :error-messages="cancelError"
            @input="cancelError = ''"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="cancelling" @click="cancelDialog = false">Annuler</v-btn>
          <v-btn color="error" :loading="cancelling" @click="confirmCancel">
            Confirmer l'annulation
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import PreloaderComponent from '../components/PreloaderComponent.vue'
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import { normalizeOrderForRate } from '../utils/orderCurrency'
import moment from 'moment'
import numeral from 'numeral'

export default {
  name: 'OrderDetailView',
  components: { PreloaderComponent, BreadcrumbComponent },
  data: () => ({
    enable: false,
    order: null,
    breadcrumbs: [
      { text: 'Restaurant', disabled: true },
      { text: 'Commandes', disabled: false, href: '/restaurant/orders' },
      { text: 'D\u00e9tails', disabled: true },
    ],
    cancelDialog: false,
    cancelReason: '',
    cancelError: '',
    cancelling: false,
    snackbar: { show: false, text: '', color: 'success', timeout: 3000 },
  }),
  computed: {
    groupedProducts() {
      if (!this.order || !this.order.order_products) return {}
      var groups = {}
      this.order.order_products.forEach(function (p) {
        var cat = p.product_category || 'Autre'
        if (!groups[cat]) groups[cat] = []
        groups[cat].push(p)
      })
      return groups
    },
    orderStatusLabel() {
      if (!this.order) return ''
      var s = (this.order.status || '').toLowerCase()
      if (s === 'open') return 'Ouverte'
      if (s === 'cancelled') return 'Annulée'
      return 'Fermée'
    },
    orderStatusChipColor() {
      if (!this.order) return 'grey'
      var s = (this.order.status || '').toLowerCase()
      if (s === 'open') return 'warning'
      if (s === 'cancelled') return 'error'
      return 'success'
    },
    customerDisplayName() {
      var o = this.order
      if (!o) return '—'
      if (o.customer && o.customer.full_name) return o.customer.full_name
      if (o.customer_name) return o.customer_name
      return '—'
    },
    customerPhone() {
      var o = this.order
      if (!o) return ''
      if (o.customer && o.customer.phone) return o.customer.phone
      return o.customer_phone || ''
    },
    customerEmail() {
      var o = this.order
      if (!o || !o.customer) return ''
      var e = o.customer.email
      return e && String(e).trim() ? e : ''
    },
    customerAddress() {
      var o = this.order
      if (!o) return ''
      if (o.customer && o.customer.address != null && String(o.customer.address).trim()) return o.customer.address
      return ''
    },
  },
  methods: {
    async load() {
      try {
        var orderId = this.$route.params.id
        var res = await this.$axios.get('/orders/' + orderId + '?include=rate,customer,server')
        this.order = normalizeOrderForRate(res.data)
        this.enable = true
      } catch (e) {
        console.error('Error loading order details:', e)
        this.enable = true
      }
    },
    fmt(v) {
      var label = 'HTG'
      if (this.order && this.order.rate && this.order.rate.name) label = this.order.rate.name
      else if (this.order && Number(this.order.rate_id) === 2) label = 'USD'
      return numeral(v || 0).format('0,0.00') + ' ' + label
    },
    formatDate(d) {
      if (!d) return '—'
      return moment(d).format('DD/MM/YYYY HH:mm')
    },
    formatTime(d) {
      if (!d) return ''
      return moment(d).format('HH:mm')
    },
    serverName(order) {
      if (order.server_details && order.server_details.name) return order.server_details.name
      if (order.server && order.server.name) return order.server.name
      return '—'
    },
    logIcon(action) {
      switch (action) {
        case 'table_opened': return 'mdi-door-open'
        case 'table_closed': return 'mdi-door-closed'
        case 'item_added': return 'mdi-plus-circle'
        case 'item_removed': return 'mdi-minus-circle'
        case 'item_qty_changed': return 'mdi-pencil'
        case 'order_cancelled': return 'mdi-cancel'
        default: return 'mdi-information'
      }
    },
    logColor(action) {
      switch (action) {
        case 'table_opened': return 'dot-green'
        case 'table_closed': return 'dot-red'
        case 'item_added': return 'dot-blue'
        case 'item_removed': return 'dot-orange'
        case 'item_qty_changed': return 'dot-yellow'
        case 'order_cancelled': return 'dot-red'
        default: return 'dot-grey'
      }
    },
    exportPDF() {
      window.print()
    },
    openCancelDialog() {
      this.cancelReason = ''
      this.cancelError = ''
      this.cancelDialog = true
    },
    currentUser() {
      try {
        return JSON.parse(localStorage.authentication || '{}').user || null
      } catch { return null }
    },
    async confirmCancel() {
      var reason = (this.cancelReason || '').trim()
      if (!reason) {
        this.cancelError = 'Veuillez indiquer une raison.'
        return
      }
      this.cancelling = true
      try {
        var user = this.currentUser()
        var payload = {
          reason: reason,
          performed_by: user ? user.full_name : null,
          performed_by_id: user ? user.id : null,
          closed_by: user ? user.id : null,
        }
        await this.$axios.post('/orders/' + this.order.id + '/cancel', payload)
        this.cancelDialog = false
        this.snackbar = { show: true, text: 'Commande annulée', color: 'success', timeout: 3000 }
        await this.load()
      } catch (e) {
        var msg = (e && e.response && e.response.data && e.response.data.message) || 'Erreur lors de l\'annulation'
        this.snackbar = { show: true, text: msg, color: 'error', timeout: 4000 }
      } finally {
        this.cancelling = false
      }
    },
  },
  mounted() {
    this.load()
  },
}
</script>

<style scoped>
.stat-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
}
.stat-label {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}
.stat-value {
  font-size: 0.9375rem;
  color: #1e293b;
  margin-top: 2px;
}
.table-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important;
  overflow: hidden;
}
.table-card .v-simple-table thead th {
  font-size: 0.6875rem !important;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8 !important;
  border-bottom: 1px solid #e8ecf1 !important;
  padding: 8px 16px !important;
}
.table-card .v-simple-table tbody td {
  font-size: 0.8125rem;
  padding: 10px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
.totals-foot td {
  font-size: 0.875rem !important;
  padding: 12px 16px !important;
  border-top: 2px solid #e8ecf1 !important;
  background: #f8fafc;
}
.category-row td {
  background: #f1f5f9 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569 !important;
  padding: 6px 16px !important;
}
.log-container {
  max-height: 500px;
  overflow-y: auto;
}
.log-entry {
  display: flex;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  align-items: flex-start;
  gap: 10px;
}
.log-entry:last-child {
  border-bottom: none;
}
.log-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
}
.dot-green { background: #4CAF50; }
.dot-red { background: #F44336; }
.dot-blue { background: #2196F3; }
.dot-orange { background: #FF9800; }
.dot-yellow { background: #FFC107; }
.dot-grey { background: #9E9E9E; }
.log-content {
  flex: 1;
}
.log-action {
  font-size: 0.8125rem;
  color: #1e293b;
}
.log-meta {
  font-size: 0.6875rem;
  color: #94a3b8;
  margin-top: 2px;
}

.customer-detail-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 0.8125rem;
}
.customer-detail-row {
  display: grid;
  grid-template-columns: 88px 1fr;
  gap: 8px;
  align-items: start;
}
.customer-detail-label {
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.customer-detail-value {
  color: #1e293b;
  word-break: break-word;
}

/* Print styles */
@media print {
  .no-print,
  .v-navigation-drawer,
  .v-app-bar,
  .app-sidebar,
  .app-topbar {
    display: none !important;
  }
  .page-content {
    padding: 0 !important;
  }
  .v-main {
    padding: 0 !important;
  }
  .order-client-card {
    break-inside: avoid;
    page-break-inside: avoid;
    border: 1px solid #ccc !important;
  }
}
</style>
